/**
 * Plain-English description of any thrown value — the words ErrorState
 * (src/components/ErrorState.tsx) puts on screen. Kept out of the component
 * file so it stays a pure, strict-checked, unit-testable function (and so the
 * component file exports only a component, which react-refresh requires).
 */
import { isApiError } from './apiError'

export type ErrorDescription = {
  title: string
  hint: string | null
  /** True when the browser never reached the server (offline, tunnel down). */
  offline: boolean
  /** Server trace id, when the backend handler produced one. */
  reference: string | null
  /** The raw text an admin may want to read. */
  technical: string
}

export function describeError(error: unknown, what = 'this'): ErrorDescription {
  if (isApiError(error)) {
    const reference = error.requestId
    const technical = error.serverMessage
      ? `${error.status} ${error.statusText} — ${error.serverMessage}`
      : error.message
    const base = { offline: false, reference, technical }
    if (error.status === 401) {
      return { ...base, title: 'Your session has ended.', hint: 'Sign in again to keep working.' }
    }
    if (error.status === 403) {
      return { ...base, title: `You don’t have access to ${what}.`, hint: 'Ask an admin if you think you should.' }
    }
    if (error.status === 404) {
      return { ...base, title: `Couldn’t find ${what}.`, hint: 'It may have been removed, or the link is out of date.' }
    }
    if (error.status === 409) {
      return { ...base, title: 'Someone else changed this just now.', hint: 'Reload to see the latest version, then try again.' }
    }
    if (error.status === 429) {
      return { ...base, title: 'ClickPay is busy right now.', hint: 'Wait a moment and try again.' }
    }
    if (error.status === 503) {
      return { ...base, title: 'ClickPay is restarting or catching up.', hint: 'This usually clears within a minute. Retry then.' }
    }
    if (error.status >= 500) {
      return { ...base, title: `ClickPay hit a problem loading ${what}.`, hint: 'Retry usually works. If it keeps failing, send the reference below to Joseph.' }
    }
    return { ...base, title: `Couldn’t load ${what}.`, hint: error.serverMessage }
  }
  if (error instanceof TypeError && /fetch|network|load failed/i.test(error.message)) {
    return { title: 'Can’t reach ClickPay.', hint: 'Check your connection, then retry.', offline: true, reference: null, technical: error.message }
  }
  const message = error instanceof Error ? error.message : String(error)
  return { title: `Couldn’t load ${what}.`, hint: 'Retry usually works.', offline: false, reference: null, technical: message }
}
